import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, FileText, Download } from "lucide-react";

const API = '/api';

export default function Sponsors() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [sponsorReport, setSponsorReport] = useState(null);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const res = await axios.get(`${API}/sponsors?t=${Date.now()}`);
      setSponsors(res.data);
    } catch (error) {
      toast.error("Failed to fetch sponsors");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSponsor) {
        await axios.put(`${API}/sponsors/${editingSponsor.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Sponsor updated");
      } else {
        await axios.post(`${API}/sponsors`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Sponsor created");
      }
      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchSponsors(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save sponsor");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sponsor?")) return;
    try {
      await axios.delete(`${API}/sponsors/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Sponsor deleted");
      setTimeout(() => fetchSponsors(), 100);
    } catch (error) {
      toast.error("Failed to delete sponsor");
    }
  };

  const openEditDialog = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      contact_name: sponsor.contact_name || "",
      email: sponsor.email || "",
      phone: sponsor.phone || "",
      address: sponsor.address || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSponsor(null);
    setFormData({
      name: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const viewReport = async (sponsor) => {
    setSelectedSponsor(sponsor);
    try {
      const res = await axios.get(`${API}/sponsors/${sponsor.id}/report`);
      setSponsorReport(res.data);
      setReportDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  const exportReportPDF = () => {
    if (!sponsorReport) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Sponsor Report - ${sponsorReport.sponsor.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Georgia', serif; padding: 40px; color: #1e293b; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #1B3B6F; }
            .header h1 { font-size: 24px; color: #1B3B6F; margin-bottom: 8px; }
            .sponsor-info { background: #f8fafc; padding: 20px; margin-bottom: 30px; border-radius: 8px; }
            .sponsor-info h2 { font-size: 18px; margin-bottom: 10px; }
            .sponsor-info p { font-size: 14px; color: #475569; margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background: #1B3B6F; color: white; padding: 12px; text-align: left; font-size: 12px; }
            td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .totals { background: #1B3B6F; color: white; padding: 20px; margin-top: 30px; border-radius: 8px; }
            .totals h3 { margin-bottom: 15px; }
            .totals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
            .total-item { text-align: center; }
            .total-item .label { font-size: 12px; opacity: 0.8; }
            .total-item .value { font-size: 24px; font-weight: bold; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #94a3b8; }
            .paid { color: #15803d; }
            .unpaid { color: #dc2626; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>NMLRA Sponsor Payment Report</h1>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
          
          <div class="sponsor-info">
            <h2>${sponsorReport.sponsor.name}</h2>
            ${sponsorReport.sponsor.contact_name ? `<p>Contact: ${sponsorReport.sponsor.contact_name}</p>` : ""}
            ${sponsorReport.sponsor.email ? `<p>Email: ${sponsorReport.sponsor.email}</p>` : ""}
            ${sponsorReport.sponsor.phone ? `<p>Phone: ${sponsorReport.sponsor.phone}</p>` : ""}
            ${sponsorReport.sponsor.address ? `<p>Address: ${sponsorReport.sponsor.address}</p>` : ""}
          </div>
          
          <h3>Sponsored Entries (${sponsorReport.total_entries})</h3>
          <table>
            <thead>
              <tr>
                <th>Shooter</th>
                <th>Event</th>
                <th>Entry Fee</th>
                <th>Status</th>
                <th>Payment Date</th>
              </tr>
            </thead>
            <tbody>
              ${sponsorReport.entries.map(entry => `
                <tr>
                  <td>${entry.shooter_name}</td>
                  <td>${entry.event_name}</td>
                  <td>$${entry.entry_fee.toFixed(2)}</td>
                  <td class="${entry.paid ? 'paid' : 'unpaid'}">${entry.paid ? 'Paid' : 'Unpaid'}</td>
                  <td>${entry.payment_date ? new Date(entry.payment_date).toLocaleDateString() : '-'}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <h3>Payment Summary</h3>
            <div class="totals-grid">
              <div class="total-item">
                <div class="label">Total Paid</div>
                <div class="value">$${sponsorReport.total_paid.toFixed(2)}</div>
              </div>
              <div class="total-item">
                <div class="label">Amount Due</div>
                <div class="value">$${sponsorReport.total_due.toFixed(2)}</div>
              </div>
              <div class="total-item">
                <div class="label">Grand Total</div>
                <div class="value">$${sponsorReport.grand_total.toFixed(2)}</div>
              </div>
            </div>
          </div>
          
          <div class="footer">
            National Muzzle Loading Rifle Association | NMLRA Trap Tracker
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="sponsors-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Sponsors
          </h1>
          <p className="text-slate-500 mt-1">Manage event sponsors and payments</p>
        </div>

        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openNewDialog}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="add-sponsor-btn"
              >
                <Plus size={20} className="mr-2" />
                Add Sponsor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {editingSponsor ? "Edit Sponsor" : "Add Sponsor"}
                </DialogTitle>
                <DialogDescription>
                  {editingSponsor ? "Update sponsor details." : "Add a new sponsor to the system."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Sponsor Name *</Label>
                  <Input
                    id="name"
                    data-testid="sponsor-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Company or Individual Name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Person</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="sponsor@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, State ZIP"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                  {editingSponsor ? "Update Sponsor" : "Add Sponsor"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Sponsors List */}
      {sponsors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No sponsors yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sponsors.map((sponsor) => (
            <Card key={sponsor.id} className="card-hover" data-testid={`sponsor-card-${sponsor.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-serif text-xl font-semibold text-slate-800">
                      {sponsor.name}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                      {sponsor.contact_name && <span>{sponsor.contact_name}</span>}
                      {sponsor.email && <span>{sponsor.email}</span>}
                      {sponsor.phone && <span>{sponsor.phone}</span>}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewReport(sponsor)}
                      className="text-green-700"
                      data-testid={`view-report-${sponsor.id}`}
                    >
                      <FileText size={16} className="mr-1" />
                      View Report
                    </Button>
                    {isAdmin() && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(sponsor)}
                        >
                          <Pencil size={16} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(sponsor.id)}
                        >
                          <Trash2 size={16} className="mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Sponsor Report: {selectedSponsor?.name}
            </DialogTitle>
          </DialogHeader>
          
          {sponsorReport && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-green-700 uppercase">Paid</p>
                    <p className="font-mono text-xl font-bold text-green-700">
                      ${sponsorReport.total_paid.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-orange-700 uppercase">Due</p>
                    <p className="font-mono text-xl font-bold text-orange-600">
                      ${sponsorReport.total_due.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-[#1B3B6F]">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-white/70 uppercase">Total</p>
                    <p className="font-mono text-xl font-bold text-white">
                      ${sponsorReport.grand_total.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Entries Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3">Shooter</th>
                      <th className="text-left p-3">Event</th>
                      <th className="text-right p-3">Fee</th>
                      <th className="text-center p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sponsorReport.entries.map((entry, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3">{entry.shooter_name}</td>
                        <td className="p-3">{entry.event_name}</td>
                        <td className="p-3 text-right font-mono">${entry.entry_fee.toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            entry.paid 
                              ? "bg-green-100 text-green-700" 
                              : "bg-orange-100 text-orange-700"
                          }`}>
                            {entry.paid ? "Paid" : "Unpaid"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button onClick={exportReportPDF} className="w-full">
                <Download size={18} className="mr-2" />
                Export PDF Report
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
